/**
 * Saúde da Operação — faixa compacta do Ao Vivo.
 * Normalidade fica neutra. Cor de alerta só quando o número pede ação.
 */
import React from 'react';
import { Activity, Clock, Gauge, Target } from 'lucide-react';

interface OpsLiveKpiStripProps {
  isLoading?: boolean;
  andonOpen: number;
  andonCritical: number;
  slaCompliance: number | null;
  slaBreachedCount: number;
  capacityAvailablePct: number | null;
  teamOverload: number;
  completedCount: number;
  completedPeriodLabel: string;
  completedFollowsRibbon?: boolean;
  onAndonClick?: () => void;
  onLoadClick?: () => void;
}

const CARD =
  'flex min-w-0 flex-col justify-between rounded-sm border border-[var(--border-default)]/70 bg-[var(--bg-surface)] px-3 py-2';

export const OpsLiveKpiStrip: React.FC<OpsLiveKpiStripProps> = ({
  isLoading = false,
  andonOpen,
  andonCritical,
  slaCompliance,
  slaBreachedCount,
  capacityAvailablePct,
  teamOverload,
  completedCount,
  completedPeriodLabel,
  completedFollowsRibbon = false,
  onAndonClick,
  onLoadClick,
}) => {
  const slaNeedsAction = slaBreachedCount > 0 || (slaCompliance != null && slaCompliance < 85);
  const slaWatch = !slaNeedsAction && slaCompliance != null && slaCompliance < 95;
  const andonNeedsAction = andonCritical > 0;
  const andonWatch = !andonNeedsAction && andonOpen > 0;
  const capacityNeedsAction = capacityAvailablePct != null && (teamOverload > 0 || capacityAvailablePct <= 0);
  const capacityWatch = capacityAvailablePct != null && !capacityNeedsAction && capacityAvailablePct < 30;

  const items = [
    {
      icon: Target,
      label: 'SLA da fila ativa',
      value: isLoading ? '…' : slaCompliance != null ? `${slaCompliance}%` : '—',
      period: 'Agora · execuções abertas',
      title: 'Percentual das execuções abertas atualmente que ainda estão dentro do SLA. Não usa o filtro de período do ribbon.',
      onClick: undefined as (() => void) | undefined,
      emphasize: slaNeedsAction,
      watch: slaWatch,
    },
    {
      icon: Activity,
      label: 'Andons abertos',
      value: isLoading ? '…' : String(andonOpen),
      period: andonCritical > 0 ? `${andonCritical} críticos · agora` : 'Agora',
      title: 'Ações abertas com andon_triggered, escalated_manager ou critical_director. O mesmo conjunto da lista. Não é uma entidade Andon separada.',
      onClick: onAndonClick,
      emphasize: andonNeedsAction,
      watch: andonWatch,
    },
    {
      icon: Gauge,
      label: 'Folga de carga',
      value: isLoading ? '…' : capacityAvailablePct == null ? '—' : `${capacityAvailablePct}%`,
      period: capacityAvailablePct == null
        ? 'Sem operadores no escopo'
        : teamOverload > 0
          ? `${teamOverload} em sobrecarga · agora`
          : 'Agora · limite de 8 tarefas ativas',
      title: 'Folga de carga simultânea. O clique abre Equipe para ver quem, ocupação, tarefas e atrasos.',
      onClick: onLoadClick,
      emphasize: capacityNeedsAction,
      watch: capacityWatch,
    },
    {
      icon: Clock,
      label: completedPeriodLabel === 'Últimos 7 dias' ? 'Concluídas · 7 dias' : 'Concluídas',
      value: isLoading ? '…' : String(completedCount),
      period: completedFollowsRibbon ? completedPeriodLabel : `${completedPeriodLabel} · não segue o filtro`,
      title: 'Tarefas concluídas. A query atual é de 7 dias quando throughput_7d existe; não há série de turno nesta visão.',
      onClick: undefined as (() => void) | undefined,
      emphasize: false,
      watch: false,
    },
  ];

  return (
    <section className="shrink-0">
      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--text-secondary)]">
        Saúde da operação
      </p>
      <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
        {items.map((item) => {
          const Icon = item.icon;
          const accent = item.emphasize
            ? 'border-l-[3px] border-l-red-500'
            : item.watch
              ? 'border-l-[3px] border-l-amber-500'
              : 'border-l-[3px] border-l-transparent';
          const valueClass = item.emphasize
            ? 'text-red-500'
            : item.watch
              ? 'text-amber-600'
              : 'text-[var(--text-primary)]';
          const Tag = item.onClick ? 'button' : 'div';
          return (
            <Tag
              key={item.label}
              type={item.onClick ? 'button' : undefined}
              onClick={item.onClick}
              className={`${CARD} ${accent} text-left ${item.onClick ? 'cursor-pointer hover:bg-[var(--button-hover)]/40' : ''}`}
              title={item.title}
            >
              <div className="flex items-center gap-1.5">
                <Icon
                  size={13}
                  className={item.emphasize ? 'text-red-500' : item.watch ? 'text-amber-600' : 'text-[var(--text-muted)]'}
                  strokeWidth={2.25}
                />
                <span className="truncate text-[9px] font-bold uppercase tracking-wider text-slate-800 dark:text-slate-100">
                  {item.label}
                </span>
              </div>
              <p className={`mt-1 text-[22px] font-bold tabular-nums leading-none ${valueClass}`}>{item.value}</p>
              <p className="mt-0.5 truncate text-[9px] font-medium text-[var(--text-muted)]">{item.period}</p>
            </Tag>
          );
        })}
      </div>
    </section>
  );
};

export default OpsLiveKpiStrip;
